import React, { ReactNode } from "react";
import { Alert, Button, Col, Container, Row, Spinner } from "react-bootstrap";
import { Sun, Moon, BookHalf } from "react-bootstrap-icons";

import { getAnalyticsWidget, createExportSchedule } from "../../../utils/Requests";
import { formatDate, getDaysAgo, getWidgetTheme } from "../../../utils/analytics";
import { DocsSidebarEntry } from "../../../types/analytics";
import { DocsSidebar, ExportButton, ScheduleExportModal, WidgetCard } from "../../../components/analytics";
import { ExportContext, ExportableData, ExportScheduleRequest } from "../../../services/export";
import Logger from "../../../utils/Logger";

/* eslint-disable camelcase */
type UtmReportRow = {
    date: string;
    custom_utm_sourceiq: string | null;
    events: number;
    visitors: number;
    users: number;
    bounce_rate: number | null;
    hours_watched: number;
};
/* eslint-enable camelcase */

type UtmReportProps = {
    standalone?: boolean;
    groups: string[];
    username: string;
    customerConfig: CustomerConfig;
};

type UtmReportState = {
    isLoading: boolean;
    error: string | null;
    startDate: string;
    endDate: string;
    appliedStartDate: string;
    appliedEndDate: string;
    isDarkTheme: boolean;
    rows: UtmReportRow[];
    sortField: keyof UtmReportRow;
    sortDirection: "asc" | "desc";
    showScheduleModal: boolean;
    sidebarOpen: boolean;
    sidebarEntries: DocsSidebarEntry[];
};

/** Default range: last 7 days inclusive of today */
function getDefaultUtmDateRange(): { startDate: string; endDate: string } {
    const today = new Date();
    const sixDaysAgo = getDaysAgo(6);
    return {
        startDate: formatDate(sixDaysAgo),
        endDate: formatDate(today)
    };
}

class UtmReport extends React.Component<UtmReportProps, UtmReportState> {
    private containerRef = React.createRef<HTMLDivElement>();

    constructor(props: UtmReportProps) {
        super(props);

        const { startDate, endDate } = getDefaultUtmDateRange();

        this.state = {
            isLoading: false,
            error: null,
            startDate,
            endDate,
            appliedStartDate: startDate,
            appliedEndDate: endDate,
            isDarkTheme: true,
            rows: [],
            sortField: "date",
            sortDirection: "desc",
            showScheduleModal: false,
            sidebarOpen: false,
            sidebarEntries: []
        };
    }

    componentDidMount(): void {
        this.fetchData();
    }

    private fetchData = async (): Promise<void> => {
        const { appliedStartDate, appliedEndDate } = this.state;

        try {
            this.setState({ isLoading: true, error: null });

            const response = await getAnalyticsWidget(
                this.props.customerConfig,
                "utm_report",
                appliedStartDate,
                appliedEndDate
            );

            const data = response as any;
            const rows: UtmReportRow[] = Array.isArray(data?.rows) ? data.rows : [];

            const sidebarEntries: DocsSidebarEntry[] = data?.widget ? [{
                widgetKey: "utm_report",
                displayName: data.widget.display_name || "UTM Report",
                description: data.widget.description || "",
                schema: data?.schema
            }] : [];

            this.setState({ rows, sidebarEntries });
        } catch (err) {
            Logger.error("Error fetching UTM report data", err);
            this.setState({ error: "Failed to fetch UTM report data" });
        } finally {
            this.setState({ isLoading: false });
        }
    };

    private handleApplyFilters = (): void => {
        this.setState(prevState => ({
            appliedStartDate: prevState.startDate,
            appliedEndDate: prevState.endDate
        }), () => {
            this.fetchData();
        });
    };

    private handleSort = (field: keyof UtmReportRow): void => {
        this.setState(prevState => ({
            sortField: field,
            sortDirection: prevState.sortField === field && prevState.sortDirection === "desc" ? "asc" : "desc"
        }));
    };

    private toggleTheme = (): void => {
        this.setState(prevState => ({ isDarkTheme: !prevState.isDarkTheme }));
    };

    private toggleSidebar = (): void => {
        this.setState(prevState => ({ sidebarOpen: !prevState.sidebarOpen }));
    };

    private getExportData = (): ExportableData[] => {
        const { rows } = this.state;

        if (rows.length === 0) {
            return [];
        }

        /* eslint-disable camelcase */
        return [{
            title: "UTM Report",
            columns: [
                { key: "date", label: "Date" },
                { key: "custom_utm_sourceiq", label: "UTM SourceIQ" },
                { key: "events", label: "Events", format: "number" },
                { key: "visitors", label: "Visitors", format: "number" },
                { key: "users", label: "Users", format: "number" },
                { key: "bounce_rate", label: "Bounce Rate", format: "percent" },
                { key: "hours_watched", label: "Hours Watched", format: "number" }
            ],
            rows,
            metadata: {
                filters: {
                    startDate: this.state.appliedStartDate,
                    endDate: this.state.appliedEndDate,
                    attributionId: [],
                    brandName: [],
                    devicePlatform: []
                },
                pageKey: "utm_report",
                customerSlug: this.props.customerConfig.slug
            }
        }];
        /* eslint-enable camelcase */
    };

    private getExportContext = (): ExportContext => {
        return {
            pageTitle: "UTM Report",
            pageKey: "utm_report",
            customerSlug: this.props.customerConfig.slug,
            filters: {
                startDate: this.state.appliedStartDate,
                endDate: this.state.appliedEndDate,
                attributionId: [],
                brandName: [],
                devicePlatform: []
            },
            getData: this.getExportData,
            getContainerRef: () => this.containerRef.current
        };
    };

    private handleScheduleSave = async (schedule: ExportScheduleRequest & { id: string }): Promise<void> => {
        await createExportSchedule(this.props.customerConfig, schedule);
    };

    private getSortedRows(): UtmReportRow[] {
        const { rows, sortField, sortDirection } = this.state;
        return [...rows].sort((a, b) => {
            const aVal = a[sortField];
            const bVal = b[sortField];

            if (aVal === null || aVal === undefined) return 1;
            if (bVal === null || bVal === undefined) return -1;

            if (typeof aVal === "number" && typeof bVal === "number") {
                return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
            }

            const aStr = String(aVal).toLowerCase();
            const bStr = String(bVal).toLowerCase();
            if (sortDirection === "asc") {
                return aStr.localeCompare(bStr);
            }
            return bStr.localeCompare(aStr);
        });
    }

    private renderSortIcon(field: keyof UtmReportRow): string {
        const { sortField, sortDirection } = this.state;
        if (sortField !== field) return "";
        return sortDirection === "asc" ? " ▲" : " ▼";
    }

    private renderTable(): ReactNode {
        const { isDarkTheme, sidebarEntries } = this.state;
        const theme = getWidgetTheme(isDarkTheme);
        const sortedRows = this.getSortedRows();

        const headerStyle: React.CSSProperties = {
            cursor: "pointer",
            userSelect: "none",
            padding: "10px 12px",
            borderBottom: `2px solid ${theme.border}`,
            color: theme.text,
            fontWeight: 600,
            fontSize: "13px",
            textAlign: "left",
            whiteSpace: "nowrap"
        };

        const cellStyle: React.CSSProperties = {
            padding: "8px 12px",
            borderBottom: `1px solid ${theme.border}`,
            color: theme.text,
            fontSize: "13px"
        };

        const numericCellStyle: React.CSSProperties = {
            ...cellStyle,
            textAlign: "right",
            fontVariantNumeric: "tabular-nums"
        };

        const numericHeaderStyle: React.CSSProperties = {
            ...headerStyle,
            textAlign: "right"
        };

        return (
            <WidgetCard
                title="UTM Report"
                description={sidebarEntries[0]?.description}
                theme={theme}
                isEmpty={sortedRows.length === 0}
                emptyMessage="No UTM data available for the selected date range."
            >
                <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                            <tr>
                                <th style={headerStyle} onClick={() => this.handleSort("date")}>
                                    Date{this.renderSortIcon("date")}
                                </th>
                                <th style={headerStyle} onClick={() => this.handleSort("custom_utm_sourceiq")}>
                                    UTM SourceIQ{this.renderSortIcon("custom_utm_sourceiq")}
                                </th>
                                <th style={numericHeaderStyle} onClick={() => this.handleSort("events")}>
                                    Events{this.renderSortIcon("events")}
                                </th>
                                <th style={numericHeaderStyle} onClick={() => this.handleSort("visitors")}>
                                    Visitors{this.renderSortIcon("visitors")}
                                </th>
                                <th style={numericHeaderStyle} onClick={() => this.handleSort("users")}>
                                    Users{this.renderSortIcon("users")}
                                </th>
                                <th style={numericHeaderStyle} onClick={() => this.handleSort("bounce_rate")}>
                                    Bounce Rate{this.renderSortIcon("bounce_rate")}
                                </th>
                                <th style={numericHeaderStyle} onClick={() => this.handleSort("hours_watched")}>
                                    Hours Watched{this.renderSortIcon("hours_watched")}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedRows.map((row, idx) => (
                                <tr key={idx} style={{ backgroundColor: idx % 2 === 0 ? "transparent" : (isDarkTheme ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)") }}>
                                    <td style={cellStyle}>{row.date}</td>
                                    <td style={cellStyle}>{row.custom_utm_sourceiq ?? "(organic / unattributed)"}</td>
                                    <td style={numericCellStyle}>{row.events.toLocaleString()}</td>
                                    <td style={numericCellStyle}>{row.visitors.toLocaleString()}</td>
                                    <td style={numericCellStyle}>{row.users.toLocaleString()}</td>
                                    <td style={numericCellStyle}>{row.bounce_rate !== null ? `${row.bounce_rate.toFixed(2)}%` : "—"}</td>
                                    <td style={numericCellStyle}>{row.hours_watched.toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </WidgetCard>
        );
    }

    public render(): ReactNode {
        const { startDate, endDate, isDarkTheme, isLoading, error, showScheduleModal, sidebarOpen, sidebarEntries } = this.state;

        return (
            <div ref={this.containerRef} style={{ display: "flex", alignItems: "flex-start", backgroundColor: isDarkTheme ? "#060606" : "#ffffff", color: isDarkTheme ? "#ffffff" : "#000000", minHeight: "100vh" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Container className="mw-100" key="utm-report" style={{ position: "relative", paddingBottom: "40px" }}>
                    <div data-export-ignore style={{ position: "absolute", right: "0px", top: "0px", zIndex: 1000, display: "flex", gap: "8px", alignItems: "center" }}>
                        <ExportButton
                            context={this.getExportContext()}
                            enabled={!isLoading && !error && this.state.rows.length > 0}
                            scheduledEnabled={this.props.customerConfig.exportConfig?.scheduledEnabled !== false}
                            onScheduleClick={() => this.setState({ showScheduleModal: true })}
                        />
                        <Button
                            onClick={this.toggleSidebar}
                            style={{ backgroundColor: "transparent", border: "none", fontSize: "20px", padding: "4px 8px", color: isDarkTheme ? "#ffffff" : "#000000" }}
                            title={sidebarOpen ? "Close Documentation" : "Open Documentation"}
                        >
                            <BookHalf />
                        </Button>
                        <Button
                            onClick={this.toggleTheme}
                            style={{ backgroundColor: "transparent", border: "none", fontSize: "24px", padding: "4px 8px", color: isDarkTheme ? "#ffffff" : "#000000" }}
                            title={isDarkTheme ? "Switch to Light Theme" : "Switch to Dark Theme"}
                        >
                            {isDarkTheme ? <Sun /> : <Moon />}
                        </Button>
                    </div>

                    <Row>
                        <Col md style={{ textAlign: "center", marginTop: "24px" }}>
                            <h2 style={{ color: isDarkTheme ? "#ffffff" : "#000000" }}>UTM Report</h2>
                        </Col>
                    </Row>

                    <Row className="justify-content-center" style={{ marginTop: "16px", marginBottom: "8px" }}>
                        <Col md={2}>
                            <label style={{ display: "block", marginBottom: "4px", fontSize: "13px", fontWeight: 600 }}>Start Date</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={(e) => this.setState({ startDate: e.target.value })}
                                style={{
                                    width: "100%",
                                    padding: "6px 8px",
                                    borderRadius: "4px",
                                    border: "1px solid #ced4da",
                                    fontSize: "14px"
                                }}
                            />
                        </Col>
                        <Col md={2}>
                            <label style={{ display: "block", marginBottom: "4px", fontSize: "13px", fontWeight: 600 }}>End Date</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={(e) => this.setState({ endDate: e.target.value })}
                                style={{
                                    width: "100%",
                                    padding: "6px 8px",
                                    borderRadius: "4px",
                                    border: "1px solid #ced4da",
                                    fontSize: "14px"
                                }}
                            />
                        </Col>
                        <Col md={1} style={{ display: "flex", alignItems: "flex-end" }}>
                            <Button
                                variant="primary"
                                onClick={this.handleApplyFilters}
                                style={{ width: "100%" }}
                            >
                                Apply
                            </Button>
                        </Col>
                    </Row>

                    {isLoading ? (
                        <Row className="justify-content-center">
                            <Col className="text-center" style={{ padding: "32px" }}>
                                <Spinner animation="border" variant="primary" />
                            </Col>
                        </Row>
                    ) : error ? (
                        <Row>
                            <Col>
                                <Alert variant="danger" style={{ marginTop: "20px" }}>{error}</Alert>
                            </Col>
                        </Row>
                    ) : (
                        <Row style={{ marginTop: "20px" }}>
                            <Col md={12}>
                                {this.renderTable()}
                            </Col>
                        </Row>
                    )}

                    <ScheduleExportModal
                        show={showScheduleModal}
                        onHide={() => this.setState({ showScheduleModal: false })}
                        filters={{
                            startDate: this.state.appliedStartDate,
                            endDate: this.state.appliedEndDate,
                            attributionId: [],
                            brandName: [],
                            devicePlatform: []
                        }}
                        pageKey="utm_report"
                        pageTitle="UTM Report"
                        customerSlug={this.props.customerConfig.slug}
                        editingSchedule={null}
                        onSave={this.handleScheduleSave}
                    />
                </Container>
              </div>
              <DocsSidebar isOpen={sidebarOpen} entries={sidebarEntries} />
            </div>
        );
    }
}

export default UtmReport;
